import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

export default function MenuPage() {
  const [data, setData] = useState({ categories: [], products: [] });
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/menu").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return data.products.filter((p) => {
      if (active !== "All" && p.category !== active) return false;
      if (needle && !p.name.toLowerCase().includes(needle) && !p.description.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [data, active, q]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      (map[p.category] ||= []).push(p);
    });
    return map;
  }, [filtered]);

  return (
    <div className="bg-white min-h-screen" data-testid="menu-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400 mb-2">The menu</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter">Pick your craving.</h1>

        <div className="mt-6 flex items-center gap-3 border-b-2 border-black py-2">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            data-testid="menu-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search dishes..."
            className="flex-1 bg-transparent outline-none text-lg placeholder:text-gray-400"
          />
        </div>

        <div className="sticky top-16 md:top-20 bg-white z-30 pt-4 pb-2 -mx-4 md:mx-0 px-4 md:px-0">
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            {["All", ...data.categories].map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`shrink-0 rounded-full px-5 py-2 text-sm font-bold border transition ${active === c ? "bg-black text-white border-black shadow-md" : "bg-white border-gray-200 text-gray-600 hover:border-black"}`}
                data-testid={`chip-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-gray-100 aspect-[4/3] animate-pulse" />
            ))}
          </div>
        )}

        {!loading && Object.keys(grouped).length === 0 && (
          <div className="py-24 text-center" data-testid="menu-empty-state">
            <div className="text-5xl mb-4">🥢</div>
            <div className="font-display font-bold text-xl">
              {q.trim()
                ? "Nothing matches"
                : active !== "All"
                ? `No items in ${active} yet`
                : "Nothing to show"}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {q.trim()
                ? "Try a different category or search term."
                : active !== "All"
                ? "We'll be adding dishes here soon — stay tuned."
                : "Try a different category or search term."}
            </p>
          </div>
        )}

        {Object.entries(grouped).map(([cat, items]) => (
          <section key={cat} className="mt-10" data-testid={`section-${cat}`}>
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-4">{cat}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
